export type ChannelType = 'EMAIL' | 'INTERNAL_MESSAGE' | 'EXTERNAL_MESSAGE';

export type TemplateType = 'HTML' | 'MARKDOWN' | 'JSON';

export type TemplateRole = 'BODY' | 'ENVELOPE';

export const CHANNELS: ChannelType[] = ['EMAIL', 'INTERNAL_MESSAGE', 'EXTERNAL_MESSAGE'];

export type Status = 'ACTIVE' | 'INACTIVE';

export type VariableFormat =
    | 'TEXT'
    | 'NUMBER'
    | 'CURRENCY'
    | 'DATE'
    | 'PHONE'
    | 'EMAIL'
    | 'GOV_TAX_ID'
    | 'SOCIAL_ID'
    | 'FILE_URL';

export const CHANNEL_LABELS: Record<ChannelType, string> = {
    EMAIL: 'E-mail',
    INTERNAL_MESSAGE: 'Mensagem interna',
    EXTERNAL_MESSAGE: 'Mensagem externa',
};

export const VARIABLE_FORMAT_LABELS: Record<VariableFormat, string> = {
    TEXT: 'Texto',
    NUMBER: 'Número',
    CURRENCY: 'Valor (R$)',
    DATE: 'Data',
    PHONE: 'Telefone',
    EMAIL: 'E-mail',
    GOV_TAX_ID: 'CNPJ',
    SOCIAL_ID: 'CPF',
    FILE_URL: 'Link de arquivo',
};

export const CHANNEL_ADAPTER: Record<ChannelType, string> = {
    EMAIL: 'SES',
    INTERNAL_MESSAGE: 'Slack',
    EXTERNAL_MESSAGE: 'WhatsApp (Blip)',
};

export const TEMPLATE_TYPE_BY_CHANNEL: Record<ChannelType, TemplateType | null> = {
    EMAIL: 'HTML',
    INTERNAL_MESSAGE: 'MARKDOWN',
    EXTERNAL_MESSAGE: null,
};

export interface TemplateVariable {
    name: string;
    label: string;
    format: VariableFormat;
    required: boolean;
    field?: string;
}

export type EmailBlockKind = 'TEXT' | 'BUTTON' | 'FOOTER';

export interface EmailBlock {
    id: string;
    kind: EmailBlockKind;
    text?: string;
    url?: string;
    align?: 'left' | 'center';
}

export type EmailEditorMode = 'BLOCKS' | 'CODE';

export interface ProviderBinding {
    placeholder: string;
    variable: string;
}

export interface ProviderTemplateButton {
    type: 'URL' | 'QUICK_REPLY';
    text: string;
}

export interface ProviderTemplate {
    name: string;
    language: string;
    category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
    status: 'APPROVED' | 'PENDING' | 'REJECTED';
    header: { type: 'NONE' | 'DOCUMENT' | 'TEXT'; text?: string };
    body: string;
    footer?: string;
    buttons: ProviderTemplateButton[];
    previousCategory?: string;
    recategorized?: boolean;
    updatedAt: string;
}

export interface SesTemplate {
    name: string;
    createdTimestamp: string;
}

export interface SesConfigurationSet {
    name: string;
}

export interface Template {
    id: string;
    name: string;
    description: string;
    channel: ChannelType;
    type: TemplateType | null;
    providerTemplateName: string | null;
    content: string;
    role?: TemplateRole;
    envelope?: EmailEnvelope;
    blocks?: EmailBlock[];
    editorMode?: EmailEditorMode;
    providerBindings?: ProviderBinding[];
    contentUnavailable?: boolean;
    variables: TemplateVariable[];
    messageCompositionId: string | null;
    status: Status;
    createdAt: string;
    updatedAt: string;
}

export interface RealTemplateVariable {
    name: string;
    display?: string;
    field?: string;
    format?: string;
    type?: string;
    size?: number;
    optional?: boolean;
}

export interface RealMailTag {
    Name: string;
    Value: string;
}

export interface RealMailBlock {
    template_name?: string;
    subject?: string;
    from?: string;
    cc?: string[];
    bcc?: string[];
    replyTo?: string[];
    attachments?: string[];
    tags?: RealMailTag[];
    configuration_set?: string;
    body?: string[];
}

export interface RealSlackBlock {
    channel: string;
    environments: string[];
    message: string;
}

export interface RealWppBlock {
    template_name?: string;
    redirect?: Record<string, unknown>;
    headers?: unknown[];
    content?: Array<{ type: string; text?: string }>;
}

export interface RealLimits {
    maxItems: number;
    truncateStrategy: string;
}

export interface RealMessageComposition {
    id: string;
    type: 'MAIL' | 'SLACK' | 'WPP';
    description?: string;
    users?: string[];
    to?: string | string[];
    active?: boolean;
    groupName?: string;
    identifier?: string[];
    groupedItems?: string[];
    attachmentZipPrefix?: string;
    templates: Array<RealMailBlock | RealSlackBlock | RealWppBlock>;
    variables?: RealTemplateVariable[];
    limits?: RealLimits;
}

export const TYPE_TO_CHANNEL: Record<RealMessageComposition['type'], ChannelType> = {
    MAIL: 'EMAIL',
    SLACK: 'INTERNAL_MESSAGE',
    WPP: 'EXTERNAL_MESSAGE',
};

export type AttachmentDelivery = 'ATTACHMENT' | 'LINK';

export interface EmailAttachment {
    source: string;
    fileName: string;
    delivery: AttachmentDelivery;
    expirationMinutes?: number;
}

export interface EmailEnvelope {
    from: string[];
    to: string[];
    cc: string[];
    bcc: string[];
    replyTo: string;
    subject: string;
    preheader: string;
    attachments: EmailAttachment[];
}

export const emptyEnvelope = (): EmailEnvelope => ({
    from: [],
    to: [],
    cc: [],
    bcc: [],
    replyTo: '',
    subject: '',
    preheader: '',
    attachments: [],
});

export interface SesTag {
    name: string;
    value: string;
}

export interface SesAdapterConfig {
    providerTemplateName: string;
    configurationSet: string;
    tags: SesTag[];
}

export interface EmailChannelConfig {
    kind: 'EMAIL';
    adapter: SesAdapterConfig;
}

export interface SlackVariant {
    channel: string;
    environments: string[];
}

export interface SlackChannelConfig {
    kind: 'INTERNAL_MESSAGE';
    variants: SlackVariant[];
}

export interface BlipAdapterConfig {
    providerTemplateName: string;
    namespace: string;
}

export interface ExternalChannelConfig {
    kind: 'EXTERNAL_MESSAGE';
    to: string[];
    adapter: BlipAdapterConfig;
}

export type ChannelConfig = EmailChannelConfig | SlackChannelConfig | ExternalChannelConfig;

export const emptyChannelConfig = (channel: ChannelType): ChannelConfig => {
    switch (channel) {
        case 'EMAIL':
            return {
                kind: 'EMAIL',
                adapter: { providerTemplateName: '', configurationSet: '', tags: [] },
            };
        case 'INTERNAL_MESSAGE':
            return { kind: 'INTERNAL_MESSAGE', variants: [] };
        case 'EXTERNAL_MESSAGE':
            return {
                kind: 'EXTERNAL_MESSAGE',
                to: [],
                adapter: { providerTemplateName: '', namespace: '' },
            };
    }
};

export const ADAPTER_SECTION_LABEL: Record<ChannelType, string> = {
    EMAIL: 'Configuração do adaptador (SES)',
    INTERNAL_MESSAGE: 'Configuração do adaptador (Slack)',
    EXTERNAL_MESSAGE: 'Configuração do adaptador (WhatsApp / Blip)',
};

export interface MessageComposition {
    id: string;
    serialId?: number;
    sendId?: string | null;
    name: string;
    description: string;
    channel: ChannelType;
    templateIds: string[];
    templates: Template[];
    groupId: string | null;
    groupName?: string | null;
    templateCount?: number;
    variableCount?: number;
    channelConfig: ChannelConfig;
    raw: RealMessageComposition;
    status: Status;
    statusReason: string | null;
    version?: number;
    createdAt: string;
    updatedAt: string;
}

export interface GroupUser {
    login: string;
    joinedAt: string;
    active: boolean;
    removedAt: string | null;
    removalReason: string | null;
}

export interface Group {
    id: string;
    name: string;
    description: string;
    defaultForNewContactPoints: boolean;
    users: GroupUser[];
    status: Status;
    statusReason: string | null;
    createdAt: string;
    updatedAt: string;
}

export type TruncateStrategy = 'link' | 'count' | 'silent' | 'full';

export type SendMode = 'IMMEDIATE' | 'SCHEDULED';

export interface RoutingCondition {
    jsonPath: string;
    equals: string;
}

export interface RoutingFieldMapping {
    eventField: string;
    variableName: string;
}

export interface RoutingTarget {
    messageCompositionId: string;
    channel: ChannelType;
    groupName: string | null;
    sendMode: SendMode;
    scheduledAt: string | null;
    groupByPayer: boolean;
    fieldMappings: RoutingFieldMapping[];
    limits: {
        maxItems: number;
        truncateStrategy: TruncateStrategy;
    };
}

export interface Routing {
    id: string;
    ruleId: string;
    event: string;
    description: string;
    conditions: RoutingCondition[];
    targets: RoutingTarget[];
    enabled: boolean;
    environments: string[];
}

export interface PageResult<T> {
    list: T[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    numberOfElements: number;
    size: number;
    apiName: string;
    apiVersion: string;
}
